import { ReactNode, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import {
  CalendlyEventType,
  CalendlyGroup,
  getCalendlyEventTypes,
  getCalendlyGroupMembers,
  getCalendlyGroups,
  getDerivedManagedEvents,
  getStagingEventTypes,
} from "../calendly-api";
import { useCursorPages } from "../hooks/use-cursor-pages";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import ThreeDotsLoader from "@/components/common/three-dots-loader";

/**
 * Read-only "live from Calendly" panel. Renders a titled card with refresh +
 * cursor pagination controls and handles its own loading / error / empty
 * states. Purely for visualising what Calendly returns — no mutations.
 */
function CalendlyPanelShell({
  title,
  subtitle,
  isLoading,
  isFetching,
  error,
  onRefresh,
  isEmpty,
  pageNumber,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  children,
}: {
  title: string;
  subtitle?: string;
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  onRefresh: () => void;
  isEmpty: boolean;
  pageNumber: number;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  children: ReactNode;
}) {
  return (
    <div className="px-6 pb-8">
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold">{title}</h2>
              <Badge variant="outline">Live · Calendly</Badge>
            </div>
            {subtitle && (
              <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={onRefresh}
            disabled={isFetching}
          >
            <RefreshCw
              className={`h-3.5 w-3.5 mr-1 ${isFetching ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-10">
            <ThreeDotsLoader />
          </div>
        ) : error ? (
          <div className="px-4 py-8 text-center text-sm text-red-600">
            {(error as any)?.response?.data?.message ||
              "Failed to load from Calendly"}
          </div>
        ) : isEmpty ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400">
            Nothing returned from Calendly.
          </div>
        ) : (
          <>
            {children}
            <div className="flex items-center justify-end gap-2 px-4 py-2 border-t border-gray-100 bg-gray-50/50">
              <span className="text-xs text-gray-400 mr-1">Page {pageNumber}</span>
              <Button
                size="sm"
                variant="outline"
                onClick={onPrev}
                disabled={!hasPrev || isFetching}
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Prev
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={onNext}
                disabled={!hasNext || isFetching}
              >
                Next <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

type CreateFromGroup = (prefill: {
  name: string;
  professional_ids: string[];
}) => void;

/** A group row that expands to show its members, each mapped to a professional. */
function GroupRow({
  group,
  onCreateFromGroup,
}: {
  group: CalendlyGroup;
  onCreateFromGroup?: CreateFromGroup;
}) {
  const [open, setOpen] = useState(false);
  const { data: members = [], isLoading, isFetching, error } = useQuery({
    queryKey: ["calendly-group-members", group.uri],
    queryFn: () => getCalendlyGroupMembers(group.uri),
    enabled: open,
    staleTime: 60_000,
  });
  const unmapped = members.filter((m) => !m.professional).length;

  return (
    <>
      <tr className="border-t border-gray-100">
        <td className="py-2 px-4">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="flex items-center gap-1 font-medium"
          >
            <ChevronRight
              className={`h-3.5 w-3.5 transition-transform ${
                open ? "rotate-90" : ""
              }`}
            />
            {group.name}
          </button>
        </td>
        <td className="py-2 px-4 font-mono text-[11px] text-gray-400 truncate max-w-[420px]">
          {group.uri}
        </td>
      </tr>
      {open && (
        <tr className="bg-gray-50/60">
          <td colSpan={2} className="px-9 py-3">
            {isLoading || isFetching ? (
              <span className="text-xs text-gray-400">Loading members…</span>
            ) : error ? (
              <span className="text-xs text-red-600">
                {(error as any)?.response?.data?.message ||
                  "Failed to load members"}
              </span>
            ) : members.length === 0 ? (
              <span className="text-xs text-gray-400">No members found.</span>
            ) : (
              <div className="space-y-1.5">
                <p className="text-xs text-gray-500">
                  {members.length} member{members.length === 1 ? "" : "s"}
                  {unmapped > 0 && (
                    <span className="text-amber-600"> · {unmapped} unmapped</span>
                  )}
                </p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                  {members.map((m) => (
                    <div key={m.uri} className="flex items-center gap-2 text-xs">
                      <span className="text-gray-700 truncate">
                        {m.name ?? m.uri}
                      </span>
                      {m.professional ? (
                        <Badge variant="active">✓ {m.professional.name}</Badge>
                      ) : (
                        <span className="text-amber-600 shrink-0">unmapped</span>
                      )}
                    </div>
                  ))}
                </div>
                {onCreateFromGroup && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2"
                    onClick={() =>
                      onCreateFromGroup({
                        name: group.name,
                        professional_ids: members
                          .filter((m) => m.professional)
                          .map((m) => m.professional!.id),
                      })
                    }
                  >
                    Create Team from this group
                    {(() => {
                      const matched = members.filter((m) => m.professional).length;
                      return ` (${matched} professional${
                        matched === 1 ? "" : "s"
                      })`;
                    })()}
                  </Button>
                )}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export function CalendlyGroupsPanel({
  onCreateFromGroup,
}: {
  onCreateFromGroup?: CreateFromGroup;
}) {
  const page = useCursorPages<CalendlyGroup>(["calendly-groups"], (t) =>
    getCalendlyGroups(t),
  );

  return (
    <CalendlyPanelShell
      title="Calendly Groups"
      subtitle="User groups in Calendly — the basis for our Teams. Expand a group to see its members and how they map to professionals."
      isLoading={page.isLoading}
      isFetching={page.isFetching}
      error={page.error}
      onRefresh={() => page.refetch()}
      isEmpty={page.items.length === 0}
      pageNumber={page.pageNumber}
      hasPrev={page.hasPrev}
      hasNext={page.hasNext}
      onPrev={page.prev}
      onNext={page.next}
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-white text-left text-xs text-gray-500">
            <th className="py-2 px-4">Name</th>
            <th className="py-2 px-4">URI</th>
          </tr>
        </thead>
        <tbody>
          {page.items.map((g) => (
            <GroupRow
              key={g.uri}
              group={g}
              onCreateFromGroup={onCreateFromGroup}
            />
          ))}
        </tbody>
      </table>
    </CalendlyPanelShell>
  );
}

const STAGING_TYPES = [
  { key: "all", label: "All" },
  { key: "solo", label: "Solo (individual)" },
  { key: "round_robin", label: "Round robin" },
] as const;

/**
 * Event types read from our staging table (not live Calendly), with type
 * (solo / round-robin) + name filters and offset pagination.
 */
export function StagingEventTypesPanel() {
  const [type, setType] = useState<"all" | "solo" | "round_robin">("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const limit = 25;

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ["staging-event-types", type, search, page],
    queryFn: () => getStagingEventTypes({ type, search, page, limit }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
  const items = data?.eventTypes ?? [];
  const total = data?.total ?? 0;
  const lastPage = data?.lastPage ?? 0;

  return (
    <div className="px-6 pb-8">
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100 gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold">Event Types</h2>
              <Badge variant="outline">staging</Badge>
              {!isLoading && !error && (
                <span className="text-xs text-gray-400">{total}</span>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-0.5">
              From the staged Calendly dump — filter by solo vs round-robin.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search name…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              className="h-8 w-52 text-sm"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCw
                className={`h-3.5 w-3.5 mr-1 ${isFetching ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-100">
          {STAGING_TYPES.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => {
                setType(t.key);
                setPage(0);
              }}
              className={`px-2.5 py-1 rounded-md text-xs border ${
                type === t.key
                  ? "bg-sidebar text-white border-sidebar"
                  : "bg-white text-gray-600 border-gray-200"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-10">
            <ThreeDotsLoader />
          </div>
        ) : error ? (
          <div className="px-4 py-8 text-center text-sm text-red-600">
            {(error as any)?.response?.data?.message || "Failed to load"}
          </div>
        ) : items.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400">
            No event types match — has the staging sync run?
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-white text-left text-xs text-gray-500">
                    <th className="py-2 px-4">Name</th>
                    <th className="py-2 px-4">Owner</th>
                    <th className="py-2 px-4">Duration</th>
                    <th className="py-2 px-4">Kind</th>
                    <th className="py-2 px-4">Pooling</th>
                    <th className="py-2 px-4">Admin-managed</th>
                    <th className="py-2 px-4">Active</th>
                    <th className="py-2 px-4">Scheduling URL</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((e) => (
                    <tr key={e.id} className="border-t border-gray-100">
                      <td className="py-2 px-4 font-medium">{e.name ?? "—"}</td>
                      <td className="py-2 px-4">
                        {e.owner_name ? (
                          <span>
                            {e.owner_type === "Team" ? "👥" : "👤"} {e.owner_name}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="py-2 px-4">
                        {e.duration_minutes ? `${e.duration_minutes}m` : "—"}
                      </td>
                      <td className="py-2 px-4">{e.kind ?? "—"}</td>
                      <td className="py-2 px-4">
                        {e.pooling_type ? (
                          <Badge variant="outline">{e.pooling_type}</Badge>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="py-2 px-4">
                        {e.admin_managed ? (
                          <Badge variant="active">yes</Badge>
                        ) : (
                          <span className="text-gray-400">no</span>
                        )}
                      </td>
                      <td className="py-2 px-4">{e.active ? "Yes" : "No"}</td>
                      <td className="py-2 px-4">
                        {e.scheduling_url ? (
                          <a
                            href={e.scheduling_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:underline text-xs truncate inline-block max-w-[220px] align-bottom"
                          >
                            {e.scheduling_url}
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-end gap-2 px-4 py-2 border-t border-gray-100 bg-gray-50/50">
              <span className="text-xs text-gray-400 mr-1">
                Page {page + 1} of {lastPage + 1}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page <= 0 || isFetching}
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Prev
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage((p) => p + 1)}
                disabled={data?.nextPage == null || isFetching}
              >
                Next <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Managed events derived from the staged event types
 * (active=true, kind=solo, pooling_type=null), grouped by name. Each row is a
 * candidate managed event; the count is how many professionals carry it.
 */
export function DerivedManagedEventsPanel({
  onCreateFromDerived,
}: {
  onCreateFromDerived?: (prefill: {
    name: string;
    duration_minutes: number | null;
  }) => void;
}) {
  const { data = [], isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ["calendly-derived-managed-events"],
    queryFn: getDerivedManagedEvents,
    staleTime: 60_000,
  });

  return (
    <div className="px-6 pb-8">
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold">
                Managed events derived from Calendly
              </h2>
              <Badge variant="outline">staging</Badge>
              {!isLoading && !error && (
                <span className="text-xs text-gray-400">{data.length}</span>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-0.5">
              active · kind=solo · pooling_type=null, grouped by name.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw
              className={`h-3.5 w-3.5 mr-1 ${isFetching ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-10">
            <ThreeDotsLoader />
          </div>
        ) : error ? (
          <div className="px-4 py-8 text-center text-sm text-red-600">
            {(error as any)?.response?.data?.message || "Failed to load"}
          </div>
        ) : data.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400">
            No managed events derived — has the staging sync run?
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-white text-left text-xs text-gray-500">
                <th className="py-2 px-4">Managed event (name)</th>
                <th className="py-2 px-4">Professionals</th>
                <th className="py-2 px-4">Duration</th>
                {onCreateFromDerived && <th className="py-2 px-4 w-28" />}
              </tr>
            </thead>
            <tbody>
              {data.map((m) => (
                <tr key={m.name ?? "—"} className="border-t border-gray-100">
                  <td className="py-2 px-4 font-medium">{m.name ?? "—"}</td>
                  <td className="py-2 px-4">{m.eventTypeCount}</td>
                  <td className="py-2 px-4">
                    {m.minDuration == null
                      ? "—"
                      : m.minDuration === m.maxDuration
                        ? `${m.minDuration}m`
                        : `${m.minDuration}–${m.maxDuration}m`}
                  </td>
                  {onCreateFromDerived && (
                    <td className="py-2 px-4">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!m.name}
                        onClick={() =>
                          onCreateFromDerived({
                            name: m.name!,
                            duration_minutes: m.minDuration,
                          })
                        }
                      >
                        Create
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function CalendlyEventTypesPanel({
  adminManaged,
  title,
  subtitle,
}: {
  adminManaged?: boolean;
  title: string;
  subtitle?: string;
}) {
  const page = useCursorPages<CalendlyEventType>(
    ["calendly-event-types", adminManaged ?? "all"],
    (t) => getCalendlyEventTypes(adminManaged, t),
  );

  return (
    <CalendlyPanelShell
      title={title}
      subtitle={subtitle}
      isLoading={page.isLoading}
      isFetching={page.isFetching}
      error={page.error}
      onRefresh={() => page.refetch()}
      isEmpty={page.items.length === 0}
      pageNumber={page.pageNumber}
      hasPrev={page.hasPrev}
      hasNext={page.hasNext}
      onPrev={page.prev}
      onNext={page.next}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white text-left text-xs text-gray-500">
              <th className="py-2 px-4">Name</th>
              <th className="py-2 px-4">Owner</th>
              <th className="py-2 px-4">Duration</th>
              <th className="py-2 px-4">Kind</th>
              <th className="py-2 px-4">Pooling</th>
              <th className="py-2 px-4">Admin-managed</th>
              <th className="py-2 px-4">Active</th>
              <th className="py-2 px-4">Scheduling URL</th>
            </tr>
          </thead>
          <tbody>
            {page.items.map((e) => (
              <tr key={e.uri} className="border-t border-gray-100">
                <td className="py-2 px-4 font-medium">{e.name}</td>
                <td className="py-2 px-4">
                  {e.owner_name ? (
                    <span>
                      {e.owner_type === "Team" ? "👥" : "👤"} {e.owner_name}
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="py-2 px-4">
                  {e.duration ? `${e.duration}m` : "—"}
                </td>
                <td className="py-2 px-4">{e.kind ?? "—"}</td>
                <td className="py-2 px-4">
                  {e.pooling_type ? (
                    <Badge variant="outline">{e.pooling_type}</Badge>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="py-2 px-4">
                  {e.admin_managed ? (
                    <Badge variant="active">yes</Badge>
                  ) : (
                    <span className="text-gray-400">no</span>
                  )}
                </td>
                <td className="py-2 px-4">{e.active ? "Yes" : "No"}</td>
                <td className="py-2 px-4">
                  {e.scheduling_url ? (
                    <a
                      href={e.scheduling_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:underline text-xs truncate inline-block max-w-[220px] align-bottom"
                    >
                      {e.scheduling_url}
                    </a>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CalendlyPanelShell>
  );
}
